import { SERVICE_REMINDER_QUEUE_TAB } from '@hetivo/hetivo-lib-types';
import type { ServiceReminderQueueTab } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

// ─── Tabs ───────────────────────────────────────────────────────────────────

/**
 * The tabs of the page in display order: All, the three urgencies, Snoozed and Completed (SWOOP-291).
 * A snoozed row also appears in its urgency tab; the Completed tab is the service history.
 */
export const SERVICE_REMINDER_TABS = [
  SERVICE_REMINDER_QUEUE_TAB.ALL,
  SERVICE_REMINDER_QUEUE_TAB.OVERDUE,
  SERVICE_REMINDER_QUEUE_TAB.DUE,
  SERVICE_REMINDER_QUEUE_TAB.UPCOMING,
  SERVICE_REMINDER_QUEUE_TAB.SNOOZED,
  SERVICE_REMINDER_QUEUE_TAB.COMPLETED,
] as const satisfies readonly ServiceReminderQueueTab[];

/** The tab the page opens on when the URL names none (decision #22: no stats-driven choice). */
export const DEFAULT_SERVICE_REMINDER_TAB: ServiceReminderQueueTab = SERVICE_REMINDER_QUEUE_TAB.ALL;

// ─── Filters ──────────────────────────────────────────────────────────────────

/** The filters of the action bar: the plan and group filters hold master-list ids. */
export interface ServiceReminderFilters {
  servicePlanIds: string[];
  vehicleGroupIds: string[];
}

export const EMPTY_SERVICE_REMINDER_FILTERS: ServiceReminderFilters = {
  servicePlanIds: [],
  vehicleGroupIds: [],
};

// ─── URL search schema ──────────────────────────────────────────────────────────

/** Longest search text the server accepts (`@MaxLength(100)` on the search DTO): a longer one is a 400. */
export const SERVICE_REMINDERS_SEARCH_MAX_LENGTH = 100;

/** Most ids the server accepts in one filter (`MAX_FILTER_IDS` on the search DTO): a longer list is a 400. */
export const SERVICE_REMINDERS_FILTER_MAX_IDS = 100;

/** A Mongo ObjectId in hex, the only shape of a plan or group id the server accepts (`@IsMongoId`). */
const MONGO_ID_PATTERN = /^[0-9a-f]{24}$/i;

const idArray = z
  .array(z.string().regex(MONGO_ID_PATTERN))
  .max(SERVICE_REMINDERS_FILTER_MAX_IDS)
  .optional()
  .catch(undefined);

/**
 * The URL search-param schema of the Service reminders page. The tab, the search text, the two filters
 * (service plan and vehicle group) and the page live in the URL, so a reload or a shared link restores the view. Every field is optional
 * and `.catch(undefined)`, so a hand-edited or stale URL degrades to the defaults (applied by the page
 * hook) instead of throwing, and a plain navigation to `/reminders` needs no search object. A search text
 * longer than the server accepts is cut to its limit rather than sent, and a filter list that is too long or holds
 * anything but a 24-hex id is dropped whole, so a stale or hand-edited link never reaches the server as a 400.
 *
 * The master selection (territory or location) is session state and deliberately NOT part of it.
 */
export const serviceRemindersListSearchSchema = z.object({
  page: z.number().int().positive().optional().catch(undefined),
  search: z
    .string()
    .transform((value) => value.slice(0, SERVICE_REMINDERS_SEARCH_MAX_LENGTH))
    .optional()
    .catch(undefined),
  servicePlanIds: idArray,
  tab: z.enum(SERVICE_REMINDER_TABS).optional().catch(undefined),
  vehicleGroupIds: idArray,
});

export type ServiceRemindersListSearch = z.infer<typeof serviceRemindersListSearchSchema>;

// ─── Conversions ────────────────────────────────────────────────────────────────

/** The display filters from the URL search params. */
export function searchToFilters(search: ServiceRemindersListSearch): ServiceReminderFilters {
  return {
    servicePlanIds: search.servicePlanIds ?? [],
    vehicleGroupIds: search.vehicleGroupIds ?? [],
  };
}

/** `[]` collapses to `undefined` so the URL stays clean (no empty arrays). */
function arrayOrUndefined(values: string[]): string[] | undefined {
  return values.length > 0 ? values : undefined;
}

/** The URL fields of the display filters, as a patch merged onto the existing search. */
export function filtersToSearch(
  filters: ServiceReminderFilters,
): Pick<ServiceRemindersListSearch, 'servicePlanIds' | 'vehicleGroupIds'> {
  return {
    servicePlanIds: arrayOrUndefined(filters.servicePlanIds),
    vehicleGroupIds: arrayOrUndefined(filters.vehicleGroupIds),
  };
}

/** True when the search text or any filter narrows the queue. */
export function hasActiveFilters(search: string | undefined, filters: ServiceReminderFilters): boolean {
  return Boolean(search?.trim()) || filters.servicePlanIds.length > 0 || filters.vehicleGroupIds.length > 0;
}
